/**
 * F09 v2 — `/verifications` Watchlist tab (§9 / E8).
 *
 * Verified entities flagged for re-review (nothing auto-revokes). The legacy
 * `legacy_rereview` reason renders as "Grandfathered — re-review". Actions:
 * Open entity, Revoke (category + reason + type-to-confirm) and Resolve
 * (`dismissed` | `resolved` + note) — the last two need `verifications:review`.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormat, useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Button,
  Chip,
  EmptyState,
  Spinner,
  Table,
  useToast,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  VERIFIABLE_ENTITY_TYPES,
  WATCHLIST_STATUSES,
  type VerifiableEntityType,
  type VerificationCandidateView,
  type WatchlistReasonCode,
  type WatchlistStatus,
} from '@bravemove/types';
import {
  adminVerificationApi,
  type VerificationListPage,
  type VerificationWatchlistQuery,
} from '../../api/verification';
import { AdminPagination } from '../../components/AdminPagination';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import {
  ResolveWatchlistDialog,
  RevokeDialog,
} from '../../components/verification/DecisionDialogs';
import {
  entityLabel,
  entityPath,
  watchlistReasonLabel,
} from '../../components/verification/labels';
import {
  EntityTypeBadge,
  VerificationErrorBanner,
} from '../../components/verification/VerificationBits';

const PER_PAGE = 25;

interface LoadState {
  readonly key: string;
  readonly page: VerificationListPage<VerificationCandidateView> | null;
  readonly error: unknown;
}

type DialogState =
  | { readonly kind: 'revoke'; readonly row: VerificationCandidateView }
  | { readonly kind: 'resolve'; readonly row: VerificationCandidateView }
  | null;

export function WatchlistTab() {
  const { t } = useI18n();
  const { formatDate } = useFormat();
  const navigate = useNavigate();
  const toast = useToast();
  const { canReview } = useVerificationPermissions();

  const [status, setStatus] = useState<WatchlistStatus>('open');
  const [entityType, setEntityType] = useState<VerifiableEntityType | null>(null);
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [state, setState] = useState<LoadState>({ key: '', page: null, error: null });

  const query: VerificationWatchlistQuery = {
    status: [status],
    ...(entityType ? { entityType } : {}),
    page,
    perPage: PER_PAGE,
  };
  const queryKey = JSON.stringify(query);
  const stateKey = `${queryKey}#${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    const key = `${queryKey}#${reloadToken}`;
    adminVerificationApi.watchlist(JSON.parse(queryKey) as VerificationWatchlistQuery).then(
      (result) => {
        if (!cancelled) setState({ key, page: result, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setState({ key, page: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [queryKey, reloadToken]);

  const loading = state.key !== stateKey;
  const rows = loading ? [] : (state.page?.items ?? []);
  const pagination = loading ? null : (state.page?.pagination ?? null);
  const reload = () => setReloadToken((n) => n + 1);

  const columns: TableColumn<VerificationCandidateView>[] = [
    {
      key: 'entity',
      header: t('admin:verification.watchlist.columns.entity'),
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold text-ink">{r.entityName}</span>
          <EntityTypeBadge type={r.entityType} />
        </div>
      ),
    },
    {
      key: 'reasons',
      header: t('admin:verification.watchlist.columns.reasons'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1" data-testid={`watchlist-reasons-${r.id}`}>
          {(r.reasonCodes as readonly WatchlistReasonCode[]).map((code) => (
            <Badge key={code} tone="warning" size="sm">
              {watchlistReasonLabel(t, code)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('admin:verification.watchlist.columns.status'),
      cell: (r) => (
        <Badge tone={r.status === 'open' ? 'warning' : 'neutral'} size="sm">
          {t(`admin:verification.watchlist.status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'flagged',
      header: t('admin:verification.watchlist.columns.flagged'),
      cell: (r) => (
        <span className="tabular-nums text-ink-2">{formatDate(new Date(r.firstSeenAt))}</span>
      ),
    },
    {
      key: 'actions',
      header: t('admin:verification.common.actions'),
      align: 'end',
      cell: (r) => (
        <div className="flex flex-wrap justify-end gap-1.5">
          <Button
            size="xs"
            variant="ghost"
            data-testid={`watchlist-open-${r.id}`}
            onClick={() => navigate(entityPath(r.entityType, r.entityId))}
          >
            {t('admin:verification.watchlist.openEntity')}
          </Button>
          {canReview ? (
            <>
              <Button
                size="xs"
                variant="destructive"
                data-testid={`watchlist-revoke-${r.id}`}
                onClick={() => setDialog({ kind: 'revoke', row: r })}
              >
                {t('admin:verification.watchlist.revoke')}
              </Button>
              {r.status === 'open' ? (
                <Button
                  size="xs"
                  variant="secondary"
                  data-testid={`watchlist-resolve-${r.id}`}
                  onClick={() => setDialog({ kind: 'resolve', row: r })}
                >
                  {t('admin:verification.watchlist.resolve')}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4" data-testid="verification-watchlist-tab">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.watchlist.statusLabel')}
        </span>
        {WATCHLIST_STATUSES.map((s) => (
          <Chip
            key={s}
            selected={status === s}
            role="button"
            tabIndex={0}
            data-testid={`watchlist-status-${s}`}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {t(`admin:verification.watchlist.status.${s}`)}
          </Chip>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.queue.entityFilterLabel')}
        </span>
        <Chip
          selected={entityType === null}
          role="button"
          tabIndex={0}
          onClick={() => {
            setEntityType(null);
            setPage(1);
          }}
        >
          {t('admin:verification.queue.allTypes')}
        </Chip>
        {VERIFIABLE_ENTITY_TYPES.map((type) => (
          <Chip
            key={type}
            selected={entityType === type}
            role="button"
            tabIndex={0}
            onClick={() => {
              setEntityType(entityType === type ? null : type);
              setPage(1);
            }}
          >
            {entityLabel(t, type)}
          </Chip>
        ))}
      </div>

      {!loading && state.error ? (
        <VerificationErrorBanner error={state.error} testId="watchlist-error" />
      ) : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <>
          <Table
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            ariaLabel={t('admin:verification.watchlist.tableAria')}
            empty={<EmptyState title={t('admin:verification.watchlist.empty')} />}
          />
          {pagination ? (
            <AdminPagination
              pagination={pagination}
              onChange={setPage}
              shownCount={rows.length}
              testId="verification-watchlist-pagination"
            />
          ) : null}
        </>
      )}

      {dialog?.kind === 'revoke' ? (
        <RevokeDialog
          entityType={dialog.row.entityType}
          entityId={dialog.row.entityId}
          entityName={dialog.row.entityName}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast.push({ tone: 'success', title: t('admin:verification.toast.revoked') });
            reload();
          }}
        />
      ) : null}
      {dialog?.kind === 'resolve' ? (
        <ResolveWatchlistDialog
          entryId={dialog.row.id}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast.push({ tone: 'success', title: t('admin:verification.toast.resolved') });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
